import { css } from 'lit'
import { csSpacing } from '@cloudsuite/mosaic-shared/styles/spacing.style.js'
import { csColorBackground, csColorBorder, csColorFunctional, csColorText } from '@cloudsuite/mosaic-shared/styles/colors.style.js'
import { csBorder } from '@cloudsuite/mosaic-shared/styles/globals.style.js'

export const style = css`
  :host {
    display: block;
    --cse-button-font-size: 15px;
    --cse-button-small-font-size: 14px;
    --cse-radio-button-size: 16px;
    --cse-radio-button-checked-background-color: rgb(77, 97, 135);
    --cse-radio-button-checked-background-color-hover: rgb(77, 97, 135);
    --cse-radio-button-checked-border-color: rgb(77, 97, 135);
    --cse-radio-button-checked-border-color-hover: rgb(77, 97, 135);
    --cse-radio-button-border-color-hover: rgb(77, 97, 135);
  }

  /* One header bar: title (left), the grid/list × desktop/mobile view toggle (centered), Import/
     Export (right). 1fr auto 1fr keeps the toggle truly centered regardless of how wide the
     title and the buttons are. */
  .product-tile-configurator-header {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: ${csSpacing.spacing06};
    width: 100%;
    box-sizing: border-box;
    padding: ${csSpacing.spacing05} ${csSpacing.spacing06};
    background: ${csColorBackground.primary};
    border-bottom: ${csBorder.width} solid ${csColorBorder.light};
  }

  .view-toggle {
    position: relative;
  }

  /* The shared tooltip's host is an invisible box laid over the hovered item (left/width set
     inline from that item's rect), so cse-tooltip anchors itself to the item without living
     inside cse-segmented-control's clipped overflow. pointer-events:none keeps it from stealing
     hover/clicks from the item underneath. */
  .view-toggle-tooltip {
    --cs-font-caption-l-font-size: 12px;
    --cs-font-caption-l-font-size-mobile: 12px;
    --cs-font-caption-l-line-height: 1.3;
    --cse-tooltip-padding-y: ${csSpacing.spacing03};
    --cse-tooltip-padding-x: ${csSpacing.spacing04};
    position: absolute;
    top: 0;
    bottom: 0;
    display: block;
    pointer-events: none;
  }

  .view-toggle-badge {
    position: absolute;
    top: 0;
    z-index: 1;
    transform: translate(-60%, -45%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 16px;
    height: 16px;
    padding: 0 ${csSpacing.spacing03};
    box-sizing: border-box;
    border-radius: 999px;
    background: ${csColorBorder.medium};
    box-shadow: 0 0 0 2px ${csColorBackground.primary};
    pointer-events: none;
  }

  .view-toggle-badge.inherited {
    opacity: 0.55;
  }

  .view-toggle-badge cse-text {
    --cse-text-color: ${csColorText.white};
    --cs-font-caption-s-font-size: 10px;
    --cs-font-caption-s-font-size-mobile: 10px;
    --cs-font-caption-s-line-height: 1;
  }

  .view-toggle-tooltip-anchor {
    display: block;
    width: 100%;
    height: 100%;
  }

  .view-toggle-icons {
    display: inline-flex;
    align-items: center;
    gap: ${csSpacing.spacing03};
  }

  .header-actions {
    display: flex;
    align-items: center;
    justify-self: end;
    gap: ${csSpacing.spacing04};
  }

  .header-actions cse-button {
    --cse-button-small-height: 32px;
    --cse-button-small-padding-y: ${csSpacing.spacing01};
    --cse-button-small-padding-x: ${csSpacing.spacing05};
  }

  /* The widget library (left) and the settings panel (right) flank the live
     canvas. Flexbox (rather than CSS grid) so the side panels' width can be
     smoothly transitioned to 0 to slide-collapse them — a grid track's size
     doesn't reliably animate the same way a flex child's own width does. */
  .configurator-layout {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    width: 100%;
    height: var(--csc-configurator-height, 80vh);
  }

  .left-panel,
  .canvas-panel,
  .sidebar-panel {
    min-height: 0;
  }

  .left-panel,
  .sidebar-panel {
    background: ${csColorBackground.primary};
    border: ${csBorder.width} solid ${csColorBorder.light};
    border-top: none;
    flex: 0 0 auto;
    width: 300px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transition: width 0.25s ease, border-width 0.25s ease;
  }

  /* --left-panel-width is the single source for the panel's width AND its content's — the
     header and widget list keep a fixed width (rather than 100%) so they don't squash while the
     panel slides shut, so they must follow every breakpoint's panel width exactly, minus the
     panel's own right border, or the header's ✕ ends up clipped past the panel edge. */
  .left-panel {
    --left-panel-width: 300px;
    width: var(--left-panel-width);
    border-left: none;
  }

  /* Slides shut via its own width (the content keeps its width, clipped by overflow:hidden),
     toggled from the rail's Widgets tab or the header's ✕. */
  .left-panel.panel-collapsed {
    width: 0;
    border-width: 0;
  }

  .left-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: none;
    width: calc(var(--left-panel-width) - ${csBorder.width});
    box-sizing: border-box;
    padding: ${csSpacing.spacing06} ${csSpacing.spacing06} ${csSpacing.spacing04};
  }

  .left-panel-header cse-text {
    --cs-font-heading-5-font-size: 15px;
    --cs-font-heading-5-font-size-mobile: 15px;
  }

  .left-panel-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    border-radius: ${csBorder.radiusMinimal};
    background: transparent;
    color: ${csColorText.body};
    font-size: 18px;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
  }

  .left-panel-close:hover {
    background: ${csColorBackground.secondary};
    color: ${csColorText.body};
  }

  .left-panel csc-tile-configurator-widget-bar {
    flex: 1;
    min-height: 0;
    width: calc(var(--left-panel-width) - ${csBorder.width});
  }

  /* Narrow icon rail on the far left — one tab per left-panel view (only Widgets for now). */
  .left-rail {
    flex: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${csSpacing.spacing04};
    width: 72px;
    box-sizing: border-box;
    padding: ${csSpacing.spacing05} ${csSpacing.spacing03};
    background: rgb(250, 251, 252);
    border: ${csBorder.width} solid ${csColorBorder.light};
    border-top: none;
  }

  .rail-tab {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${csSpacing.spacing03};
    width: 56px;
    height: 56px;
    padding: 0;
    border: none;
    border-radius: ${csBorder.radiusMedium};
    background: transparent;
    color: ${csColorText.headings};
    cursor: pointer;
    transition: background-color 0.15s ease;
  }

  .rail-tab cse-icon {
    font-size: 20px;
  }

  .rail-tab-label {
    --cse-text-color: ${csColorText.headings};
    --cs-font-caption-m-font-size: 10px;
    --cs-font-caption-m-font-size-mobile: 10px;
    --cs-font-caption-m-font-weight: 700;
  }

  .rail-tab:hover {
    background: ${csColorBackground.secondary};
  }

  .rail-tab.active {
    background: rgb(232, 236, 243);
  }

  /* The right panel hosts both the area/zone settings (csc-tile-configurator-zone-panel) and the
     widget settings (csc-tile-configurator-sidebar), but only one ever renders content for the
     current selection — the other renders nothing. So the panel itself scrolls, and both hosts
     size to their content instead of each claiming the full height. */
  /* Unlike the left panel this one never slides shut, so its content simply takes the panel's
     own width (never a fixed px width, which overflowed the border-box panel and put the
     scrollbar on top of it) — with width:100% the content narrows beside a scrollbar only when
     one actually appears. Don't add scrollbar-gutter:stable: it reserves that space even with no
     scrollbar, leaving full-width content (the property accordion) visibly short of the edge. */
  .sidebar-panel {
    overflow-y: auto;
    --cs-form-control-font-size: 15px;
    --cs-font-body-font-size: 15px;
    --cs-font-body-font-size-mobile: 15px;
  }

  .sidebar-panel csc-tile-configurator-zone-panel,
  .sidebar-panel csc-tile-configurator-sidebar {
    flex: none;
    height: auto;
    overflow: visible;
    width: 100%;
  }

  .canvas-panel-wrapper {
    position: relative;
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .canvas-panel {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    align-content: flex-start;
    justify-content: center;
    flex-wrap: wrap;
    flex: 1;
    column-gap: ${csSpacing.spacing09};
    row-gap: ${csSpacing.spacing06};
    min-width: 0;
    padding: ${csSpacing.spacing06};
    overflow-y: auto;
    overflow-x: auto;
    background-color: ${csColorBackground.secondary};
    background-image: radial-gradient(#d5dce4 1px, transparent 1px);
    background-size: 20px 20px;
  }

  /* Its own full-width row at the top of the canvas (width:100% + .canvas-panel's flex-wrap — not flex-basis:100%, which would mean full *height*
     once the narrow-screen breakpoint switches .canvas-panel to a column) while a mobile
     view is active, so it pushes the tile preview down instead of covering it. The canvas is fully
     inert then (see MobileModeContextType) — this says so rather than leaving clicks to silently
     do nothing. The note itself is auto-width and never wraps. */
  .mobile-preview-note-row {
    flex: none;
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .mobile-preview-note {
    display: flex;
    align-items: center;
    gap: ${csSpacing.spacing04};
    width: max-content;
    padding: ${csSpacing.spacing03} ${csSpacing.spacing05};
    border-radius: 999px;
    background: ${csColorBackground.inverted};
    color: ${csColorText.white};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
    white-space: nowrap;
  }

  .mobile-preview-note cse-text {
    --cse-text-color: ${csColorText.white};
  }

  .mobile-note-action {
    flex: none;
    padding: ${csSpacing.spacing02} ${csSpacing.spacing05};
    border: ${csBorder.width} solid rgba(255, 255, 255, 0.5);
    border-radius: 999px;
    background: transparent;
    color: ${csColorText.white};
    font-family: var(--csc-product-tile-configurator-note-action-font-family, var(--cs-font-body-s-font-family, var(--cs-font-family-body, Roboto, Arial, sans-serif)));
    font-size: 12px;
    cursor: pointer;
  }

  .mobile-note-action:hover,
  .mobile-note-action:focus-visible {
    background: rgba(255, 255, 255, 0.15);
  }

  .canvas-column {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${csSpacing.spacing08};
    flex-shrink: 0;
  }

  /* Bottom-right of the (non-scrolling) canvas wrapper, so it stays put while the canvas scrolls.
     Hover-only: hovering sets cleanPreview, leaving clears it — no click/toggle state. */
  .configurator-toast {
    position: absolute;
    left: 50%;
    bottom: ${csSpacing.spacing06};
    z-index: 3;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: ${csSpacing.spacing05};
    max-width: calc(100% - 2 * ${csSpacing.spacing08});
    padding: ${csSpacing.spacing04} ${csSpacing.spacing04} ${csSpacing.spacing04} ${csSpacing.spacing06};
    border-radius: ${csBorder.radiusMedium};
    background: ${csColorBackground.inverted};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  .configurator-toast cse-text {
    --cse-text-color: ${csColorText.white};
  }

  .configurator-toast-action,
  .configurator-toast-close {
    flex: none;
    border: none;
    background: transparent;
    color: ${csColorText.white};
    font: inherit;
    cursor: pointer;
  }

  .configurator-toast-action {
    font-family: var(--csc-product-tile-configurator-toast-action-font-family, var(--cs-font-body-s-font-family, var(--cs-font-family-body, Roboto, Arial, sans-serif)));
    font-size: var(--csc-product-tile-configurator-toast-action-font-size, var(--cs-font-body-s-font-size, 14px));
    font-weight: var(--csc-product-tile-configurator-toast-action-font-weight, var(--cs-font-body-s-font-weight, 400));
    line-height: var(--csc-product-tile-configurator-toast-action-line-height, var(--cs-font-body-s-line-height, 1.5));
    padding: ${csSpacing.spacing03} ${csSpacing.spacing05};
    border: ${csBorder.width} solid rgba(255, 255, 255, 0.5);
    border-radius: ${csBorder.radiusMedium};
  }

  .configurator-toast-action:hover,
  .configurator-toast-action:focus-visible {
    background: rgba(255, 255, 255, 0.1);
  }

  .configurator-toast-close {
    display: flex;
    padding: ${csSpacing.spacing03};
  }

  .clean-preview-button {
    position: absolute;
    right: ${csSpacing.spacing06};
    bottom: ${csSpacing.spacing06};
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    border: ${csBorder.width} solid ${csColorBorder.light};
    border-radius: 50%;
    background: ${csColorBackground.primary};
    color: ${csColorText.body};
    font-size: 16px;
    cursor: default;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
    transition: background-color 0.15s ease, color 0.15s ease;
  }

  .clean-preview-button.active {
    background: ${csColorBackground.inverted};
    color: ${csColorText.white};
  }

  .canvas-column[hidden] {
    display: none;
  }

  .canvas-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${csSpacing.spacing04};
    width: 100%;
  }

  cse-paper {
    transition: all 0.2s ease;
  }

  .paper-content {
    width: 100%;
    height: 100%;
  }

  /* JSON Configuration Display */
  .json-display {
    box-sizing: border-box;
    height: min(360px, 50vh);
    overflow: auto;
    margin: 0;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 12px;
    line-height: 1.4;
    color: ${csColorText.body};
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  .export-unconfigured-warnings {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .export-unconfigured-warning {
    --cse-alert-padding: 10px 12px;
    --cse-alert-column-gap: 12px;
    --cse-alert-row-gap: 0;
    --cs-font-heading-5-font-size: 14px;
    --cs-font-heading-5-font-size-mobile: 14px;
    --cs-font-body-font-size: 12px;
    --cs-font-body-font-size-mobile: 12px;
  }

  .json-input {
    cursor: text;
    outline: none;
  }

  .json-input:empty::before {
    content: attr(data-placeholder);
    color: ${csColorText.placeholder};
  }

  .modal-body-content cse-paper:focus-within {
    outline: 2px solid ${csColorFunctional.info};
    outline-offset: 2px;
  }

  /* Import/Export Modal */
  cse-modal {
    --cse-modal-large-width: 960px;
  }
  
  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  /* cse-modal's own .body centers its slotted content instead of stretching it
     (align-items: center), so without an explicit width our content — the JSON
     display, textarea, etc. — stays at its intrinsic size no matter how wide
     the modal itself gets, leaving the header/body padding looking oversized
     next to it. Force our slotted wrapper to actually fill the modal. */
  .modal-body-content {
    width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    row-gap: ${csSpacing.spacing05};
  }

  .modal-error-text {
    --cse-text-color: ${csColorFunctional.error}
  }

  /* Responsive breakpoints */
  @media (max-width: 1200px) {
    .left-panel {
      --left-panel-width: 260px;
    }

    .sidebar-panel {
      width: 260px;
    }
  }

  @media (max-width: 900px) {
    .product-tile-configurator-header {
      display: flex;
      flex-wrap: wrap;
    }

    .header-actions {
      margin-left: auto;
    }

    .configurator-layout {
      flex-direction: column;
    }

    .left-rail {
      display: none;
    }

    .canvas-panel {
      order: 1;
      flex-direction: column;
      align-items: center;
    }

    .left-panel,
    .sidebar-panel {
      width: 100%;
    }

    /* The rail is hidden at this breakpoint (panels always stack full-width here), so a
       closed widget panel carried over from a wider viewport must not stay stuck at 0 width
       with no way back. */
    .left-panel.panel-collapsed {
      width: 100%;
      border-width: ${csBorder.width};
    }

    .left-panel-close {
      display: none;
    }

    .left-panel-header {
      width: 100%;
    }

    .left-panel csc-tile-configurator-widget-bar {
      width: 100%;
    }

    .left-panel {
      order: 2;
    }

    .sidebar-panel {
      order: 3;
    }
  }
`
